/**
 * Position map — for each texel of the 585×559 template, the 3D point on the
 * body that samples it.
 *
 * Built by rasterising each triangle in UV space and barycentrically
 * interpolating world position, on the CPU rather than as a GPU render pass.
 * Three reasons:
 *
 *   · Testable in Node. A GPU pass would only be verifiable in a browser, and
 *     the bugs that have actually bitten this project (rotated body, floating
 *     limbs) were all geometry-assembly bugs a headless test can catch.
 *   · No float-render-target dependency. That feature is uneven on exactly the
 *     low-end hardware this audience uses.
 *   · Fast enough. A six-box body is ~72 triangles; this runs in milliseconds.
 *
 * Swap to a GPU pass only if a high-poly body makes it necessary.
 *
 * Unlocks three features that would otherwise each need their own solution:
 * 3D brush wrapping across seams, seam-continuous patterns, and the AO bake.
 */

/** A template rectangle. Declared here so this module stays import-free. */
export interface TexelRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MeshGeometry {
  /** World-space positions, one triple per vertex. */
  positions: Array<[number, number, number]>;
  /** UVs, glTF convention: origin top-left, v increases downward. */
  uvs: Array<[number, number]>;
  /** Triangle indices. */
  indices: number[];
  /** Used to attribute a texel back to a body part. */
  name: string;
}

export interface PositionMap {
  width: number;
  height: number;
  /** x, y, z, valid(0|1) per texel, row-major from the top. */
  data: Float32Array;
  /** Index into the `parts` list, or -1. Lets a texel name its part. */
  part: Int8Array;
  parts: string[];
}

export function createPositionMap(
  meshes: MeshGeometry[],
  width: number,
  height: number,
  seal?: { regions: Record<string, TexelRect>; radius?: number },
): PositionMap {
  const data = new Float32Array(width * height * 4);
  const part = new Int8Array(width * height).fill(-1);
  const parts = meshes.map((m) => m.name);

  meshes.forEach((mesh, meshIndex) => {
    for (let t = 0; t + 2 < mesh.indices.length; t += 3) {
      const ia = mesh.indices[t] as number;
      const ib = mesh.indices[t + 1] as number;
      const ic = mesh.indices[t + 2] as number;
      const uvA = mesh.uvs[ia];
      const uvB = mesh.uvs[ib];
      const uvC = mesh.uvs[ic];
      const pA = mesh.positions[ia];
      const pB = mesh.positions[ib];
      const pC = mesh.positions[ic];
      if (!uvA || !uvB || !uvC || !pA || !pB || !pC) continue;

      rasterise(data, part, meshIndex, width, height, uvA, uvB, uvC, pA, pB, pC);
    }
  });

  const map: PositionMap = { width, height, data, part, parts };
  /*
    28, not 6. The radius has to clear whatever the flat face is inset by, and
    that is a property of the body rather than a constant.

    R6's bevel is 4.2 texels and 6 was enough. It was not enough for a shaped
    torso or for the official R15 limbs — measured at 1,634 and 2,712 texels
    left unreachable, every one of them a place a brush stroke would have
    landed on nothing. 12 got those to 144 and 80; 20 closed both completely.

    20 was in turn not enough for the anthro rig, whose waist tapers: the front
    face narrows toward the bottom of the UpperTorso band, so the outer columns
    of the last ~16 rows have no forward-facing geometry at all. That is a
    silhouette, not a bevel — 323 texels in the bottom corners of torso.front
    and torso.back. 24 leaves 30; 28 closes it.

    Raising this is free for the bodies already covered. `sealRegionGaps` is an
    iterative one-texel dilation that stops the moment a pass fills nothing, so
    a body that seals in 20 passes never runs a 21st. Filling stays bounded by
    the band, so a larger radius still cannot reach into a neighbouring part.
  */
  if (seal) sealRegionGaps(map, seal.regions, seal.radius ?? 28);
  return map;
}

/** Half-open pixel-centre rasterisation with barycentric interpolation. */
function rasterise(
  data: Float32Array,
  part: Int8Array,
  meshIndex: number,
  width: number,
  height: number,
  uvA: [number, number],
  uvB: [number, number],
  uvC: [number, number],
  pA: [number, number, number],
  pB: [number, number, number],
  pC: [number, number, number],
) {
  const ax = uvA[0] * width;
  const ay = uvA[1] * height;
  const bx = uvB[0] * width;
  const by = uvB[1] * height;
  const cx = uvC[0] * width;
  const cy = uvC[1] * height;

  const area = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  if (Math.abs(area) < 1e-9) return; // degenerate in UV space (e.g. the head)

  /*
    Bounds are the triangle's own footprint — no outward expansion.

    This used to grow by a texel so region boundary rows carried data, which was
    the right fix when nothing else covered them. It stopped being right when
    R15 started slicing one region between two parts: the expansion let
    UpperTorso spill a row past its band, so torso.front row 170 was claimed by
    both halves and a brush there could paint one part's rows onto the other's
    geometry.

    sealRegionGaps now fills boundary rows properly and refuses to cross a part
    boundary while doing it, so the expansion is redundant as well as harmful.
  */
  const minX = Math.max(0, Math.floor(Math.min(ax, bx, cx)));
  const maxX = Math.min(width - 1, Math.ceil(Math.max(ax, bx, cx)));
  const minY = Math.max(0, Math.floor(Math.min(ay, by, cy)));
  const maxY = Math.min(height - 1, Math.ceil(Math.max(ay, by, cy)));

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const px = x + 0.5;
      const py = y + 0.5;

      let w0 = ((bx - px) * (cy - py) - (by - py) * (cx - px)) / area;
      let w1 = ((cx - px) * (ay - py) - (cy - py) * (ax - px)) / area;
      let w2 = 1 - w0 - w1;

      // A small tolerance keeps the shared edge between two triangles covered
      // rather than leaving a one-texel seam down the middle of every quad.
      const tol = -0.002;
      if (w0 < tol || w1 < tol || w2 < tol) continue;

      // Clamp so an edge texel takes the boundary value instead of extrapolating.
      w0 = Math.max(0, w0);
      w1 = Math.max(0, w1);
      w2 = Math.max(0, w2);
      const sum = w0 + w1 + w2 || 1;
      w0 /= sum;
      w1 /= sum;
      w2 /= sum;

      const o = (y * width + x) * 4;
      data[o] = w0 * pA[0] + w1 * pB[0] + w2 * pC[0];
      data[o + 1] = w0 * pA[1] + w1 * pB[1] + w2 * pC[1];
      data[o + 2] = w0 * pA[2] + w1 * pB[2] + w2 * pC[2];
      data[o + 3] = 1;
      part[y * width + x] = meshIndex;
    }
  }
}

/**
 * Fill the unmapped border a bevelled part leaves inside each region.
 *
 * A real Roblox part is a bevelled box, and Roblox projects clothing across the
 * part's NOMINAL size — so the flat face lands inset by the bevel, about 4
 * texels here, and the outer ring of every region is covered only by chamfer
 * polygons. Those chamfers sit at exactly 45 degrees between two faces, so
 * whichever face wins the dominant-axis test takes them and the other face's
 * region is left with a 4-texel hole around its edge.
 *
 * The hole matters because it is precisely where brush wrapping happens: a
 * stroke near a seam needs position data at the region boundary, and without
 * this a stroke at the silhouette edge simply stops.
 *
 * Filling is per region and never reads across a boundary. That restriction is
 * the whole point — the gutter is only 2px, so an unrestricted dilation would
 * happily hand torso.front's edge a position from torso.left, which is a
 * different face of the body and would send paint to the wrong place.
 */
function sealRegionGaps(
  map: PositionMap,
  regions: Record<string, TexelRect>,
  radius: number,
): void {
  const { width, data, part } = map;

  for (const rect of Object.values(regions)) {
    const x0 = rect.x;
    const y0 = rect.y;
    const x1 = rect.x + rect.w;
    const y1 = rect.y + rect.h;

    for (let pass = 0; pass < radius; pass++) {
      // Snapshot per pass so a filled texel cannot seed another fill in the
      // same pass, which would let one mapped texel streak across the region.
      const filled: Array<[number, number, number]> = [];
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const i = y * width + x;
          if (data[i * 4 + 3] === 1) continue;

          /*
            Fill only where the neighbours agree on which part owns this texel.

            A region is not always one part. On R15 the torso region is split
            into bands — UpperTorso takes the top 96 rows, LowerTorso the rest —
            and an unrestricted fill let the upper half dilate down across that
            line, so row 170 ended up claimed by both. A brush there would have
            painted LowerTorso's rows onto UpperTorso's geometry.

            Where neighbours disagree the texel is a genuine boundary, and
            leaving it unmapped is the honest answer: better a hairline the
            brush cannot reach than a texel wired to the wrong part.
          */
          let best = -1;
          let owner = -2;
          let contested = false;
          for (let dy = -1; dy <= 1 && !contested; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const nx = x + dx;
              const ny = y + dy;
              if (nx < x0 || ny < y0 || nx >= x1 || ny >= y1) continue;
              const n = ny * width + nx;
              if (data[n * 4 + 3] !== 1) continue;
              const p = part[n] as number;
              if (owner === -2) {
                owner = p;
                best = n;
              } else if (p !== owner) {
                contested = true;
                break;
              }
            }
          }
          if (best >= 0 && !contested) filled.push([i, best, 0]);
        }
      }
      if (filled.length === 0) break;
      for (const [i, from] of filled) {
        data[i * 4] = data[from * 4] as number;
        data[i * 4 + 1] = data[from * 4 + 1] as number;
        data[i * 4 + 2] = data[from * 4 + 2] as number;
        data[i * 4 + 3] = 1;
        part[i] = part[from] as number;
      }
    }
  }
}

/** World position for a texel, or null where nothing maps. */
export function positionAt(
  map: PositionMap,
  x: number,
  y: number,
): [number, number, number] | null {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) return null;
  const i = (Math.floor(y) * map.width + Math.floor(x)) * 4;
  if (map.data[i + 3] !== 1) return null;
  return [map.data[i] as number, map.data[i + 1] as number, map.data[i + 2] as number];
}

export function partAt(map: PositionMap, x: number, y: number): string | null {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) return null;
  const p = map.part[Math.floor(y) * map.width + Math.floor(x)] ?? -1;
  return p < 0 ? null : (map.parts[p] ?? null);
}

/** Fraction of texels that carry a position. Cheap coverage sanity check. */
export function coverage(map: PositionMap): number {
  let n = 0;
  for (let i = 3; i < map.data.length; i += 4) if (map.data[i] === 1) n++;
  return n / (map.width * map.height);
}
/** Median distance between horizontally adjacent mapped texels, in studs. */
export function texelScale(map: PositionMap): number {
  const samples: number[] = [];
  const { width, height } = map;
  // A coarse sweep is plenty: this only sets a tolerance, and sampling every
  // texel would cost more than building the grid.
  for (let y = 0; y < height; y += 7) {
    for (let x = 0; x < width - 1; x += 7) {
      const a = positionAt(map, x, y);
      const b = positionAt(map, x + 1, y);
      if (!a || !b) continue;
      const d = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
      // Skip pairs that straddle a region boundary: those jump across the body
      // and would drag the median up by orders of magnitude.
      if (d > 1e-6 && d < 0.5) samples.push(d);
    }
  }
  if (samples.length === 0) return 1 / 64; // the measured R6 density
  samples.sort((a, b) => a - b);
  return samples[samples.length >> 1] as number;
}
